import React from "react";
import ReliableChatImage from "../ReliableChatImage";

const CELL_BASE =
  "relative overflow-hidden cursor-pointer hover:brightness-95 transition-[filter] duration-150 bg-gray-100";
const IMG_CLASS = "w-full h-full object-cover block";
const CONTAINER_BASE = "rounded-lg overflow-hidden";

const Cell = ({
  src,
  previewSrc,
  index,
  imageRef,
  onImageClick,
  onImageResolved,
  extraClass = "",
  overlay = null,
}) => (
  <div
    className={`${CELL_BASE} ${extraClass}`}
    onClick={(event) => onImageClick?.(event, index)}
  >
    <ReliableChatImage
      src={src}
      previewSrc={previewSrc}
      alt=""
      className={IMG_CLASS}
      containerClassName="w-full h-full"
      fallbackClassName="text-[11px]"
      onResolved={() => onImageResolved?.(imageRef)}
    />
    {overlay}
  </div>
);

const ImageGridAlbum = ({
  imgs,
  previewImgs = [],
  imageRefs = [],
  onImageClick,
  onImageResolved,
}) => {
  if (!imgs || imgs.length === 0) return null;

  const count = imgs.length;
  const getPreviewSrc = (index) => previewImgs?.[index];
  const getImageRef = (index) => imageRefs?.[index];

  if (count === 1) {
    return (
      <div className="max-w-[320px]">
        <ReliableChatImage
          src={imgs[0]}
          previewSrc={getPreviewSrc(0)}
          alt=""
          className="max-h-[320px] max-w-full rounded-lg cursor-pointer hover:brightness-95 transition-[filter] duration-150 block"
          containerClassName="rounded-lg"
          onClick={(event) => onImageClick?.(event, 0)}
          onResolved={() => onImageResolved?.(getImageRef(0))}
        />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div
        className={`${CONTAINER_BASE} w-[320px] max-w-full grid grid-cols-2 gap-[2px] aspect-[2/1]`}
      >
        <Cell
          src={imgs[0]}
          previewSrc={getPreviewSrc(0)}
          index={0}
          imageRef={getImageRef(0)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
        />
        <Cell
          src={imgs[1]}
          previewSrc={getPreviewSrc(1)}
          index={1}
          imageRef={getImageRef(1)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
        />
      </div>
    );
  }

  if (count === 3) {
    return (
      <div
        className={`${CONTAINER_BASE} w-[320px] max-w-full grid grid-cols-2 grid-rows-2 gap-[2px] aspect-square`}
      >
        <Cell
          src={imgs[0]}
          previewSrc={getPreviewSrc(0)}
          index={0}
          imageRef={getImageRef(0)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
          extraClass="row-span-2"
        />
        <Cell
          src={imgs[1]}
          previewSrc={getPreviewSrc(1)}
          index={1}
          imageRef={getImageRef(1)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
        />
        <Cell
          src={imgs[2]}
          previewSrc={getPreviewSrc(2)}
          index={2}
          imageRef={getImageRef(2)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
        />
      </div>
    );
  }

  if (count === 4) {
    return (
      <div
        className={`${CONTAINER_BASE} w-[320px] max-w-full grid grid-cols-2 grid-rows-2 gap-[2px] aspect-square`}
      >
        {imgs.map((img, index) => (
          <Cell
            key={`${index}_${img}`}
            src={img}
            previewSrc={getPreviewSrc(index)}
            index={index}
            imageRef={getImageRef(index)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
        ))}
      </div>
    );
  }

  if (count === 5) {
    return (
      <div
        className={`${CONTAINER_BASE} w-[320px] max-w-full flex flex-col gap-[2px]`}
      >
        <div className="grid grid-cols-2 gap-[2px] aspect-[2/1]">
          <Cell
            src={imgs[0]}
            previewSrc={getPreviewSrc(0)}
            index={0}
            imageRef={getImageRef(0)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
          <Cell
            src={imgs[1]}
            previewSrc={getPreviewSrc(1)}
            index={1}
            imageRef={getImageRef(1)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
        </div>
        <div className="grid grid-cols-3 gap-[2px] aspect-[3/1]">
          <Cell
            src={imgs[2]}
            previewSrc={getPreviewSrc(2)}
            index={2}
            imageRef={getImageRef(2)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
          <Cell
            src={imgs[3]}
            previewSrc={getPreviewSrc(3)}
            index={3}
            imageRef={getImageRef(3)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
          <Cell
            src={imgs[4]}
            previewSrc={getPreviewSrc(4)}
            index={4}
            imageRef={getImageRef(4)}
            onImageClick={onImageClick}
            onImageResolved={onImageResolved}
          />
        </div>
      </div>
    );
  }

  const displayImgs = imgs.slice(0, 6);
  const extraCount = imgs.length - 6;

  return (
    <div
      className={`${CONTAINER_BASE} w-[320px] max-w-full grid grid-cols-3 grid-rows-2 gap-[2px] aspect-[3/2]`}
    >
      {displayImgs.map((img, index) => (
        <Cell
          key={`${index}_${img}`}
          src={img}
          previewSrc={getPreviewSrc(index)}
          index={index}
          imageRef={getImageRef(index)}
          onImageClick={onImageClick}
          onImageResolved={onImageResolved}
          overlay={
            index === 5 && extraCount > 0 ? (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-2xl font-semibold pointer-events-none">
                +{extraCount}
              </div>
            ) : null
          }
        />
      ))}
    </div>
  );
};

export default ImageGridAlbum;
